import { useCallback, useMemo, useState } from 'react';
import { useConfirmCandidateData } from '~/components/ConfirmCandidate/hooks/useConfirmCandidateData';
import { CredentialingV2, CredentialingV2DNTApi } from '~/constants/ff.posthog';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { useGetAuthorizationCredentials } from '~/domain/Credentials/usecases/useGetAuthorizationCredentials';
import { useCandidateOvertime } from '~/domain/Jobs/useCases/useCandidateOvertime';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { IJob, IMultipleCounters } from '~/models';

interface IUseConfirmCandidatesDataArgs {
  shiftId?: string;
  /**
   * The route's `:candidateId`, when it names one.
   *
   * Honoured here but deliberately not in the legacy screen's sense: that screen
   * rewrote the param to the first applicant as soon as the list settled, which
   * is why this page ignored it at first. The redesign never rewrites it, so the
   * only things that can set it are an office following a deep link from an
   * email, and the decline mutation advancing to the next candidate — both of
   * which are a choice worth honouring, but only while that candidate is still
   * an applicant. A link outlives the application it points at.
   */
  routeCandidateId?: string;
}

/**
 * The page's reads, plus which of its two views they are being read for.
 *
 * Data and selection are one hook because they are genuinely coupled: the
 * selected candidate scopes the fetch. `useConfirmCandidateData` takes a
 * `candidateId` to load that candidate's overtime and credentials, so the
 * selection has to exist *before* the query runs — which is why the sole
 * applicant is adopted into the selection state rather than derived below the
 * call. Deriving it there leaves the two disagreeing, and the fetch is what
 * loses: see `openCandidateId`.
 *
 * The whole one-vs-many question is answered here: with several applicants the
 * list is the landing page and opening a row selects a candidate; with exactly
 * one there is nothing to choose, so that candidate *is* the selection.
 */
export const useConfirmCandidatesData = ({
  shiftId,
  routeCandidateId,
}: IUseConfirmCandidatesDataArgs) => {
  /**
   * Which candidate's own page is open, or `null` for the list.
   *
   * Page state rather than a route param: the confirm route already carries a
   * `:candidateId`, but the legacy screen rewrites it to the first applicant as
   * soon as the list settles, so it records no choice the office made.
   */
  const [openCandidateId, setOpenCandidateId] = useState<string | null>(null);

  const { job, allCandidates, loadingCandidates, isSuccessFetchJobCandidates, authorizations } =
    useConfirmCandidateData({ jobId: shiftId, candidateId: openCandidateId ?? undefined });

  /**
   * `useConfirmCandidateData` is JavaScript, so its React Query payloads arrive
   * untyped. Naming the shape once here keeps the assertion at the boundary
   * instead of at every use.
   *
   * Memoised because it feeds `useCandidateImpression`: the `?? []` would otherwise
   * mint a new array on every render and re-run its position-freeze effect and
   * re-create its click callback.
   */
  const counters = useMemo(() => (allCandidates ?? []) as IMultipleCounters[], [allCandidates]);

  const isSingleCandidate = counters.length === 1;

  const soleCandidateId = isSingleCandidate ? (counters[0]?.candidate?.id ?? null) : null;

  const isRouteCandidateLive =
    Boolean(routeCandidateId) &&
    counters.some((counter) => counter?.candidate?.id === routeCandidateId);

  /**
   * The one place `openCandidateId` is decided, so the two things that can
   * decide it cannot fight over it.
   *
   * It scopes the credentials and overtime queries, so a candidate on screen
   * without being *selected* renders from data fetched for somebody else, or for
   * nobody: an empty Credentials tab, and a receipt carrying the shift's fees
   * rather than their overtime-merged ones.
   *
   * In precedence:
   *
   * 1. **The route, when it names a current applicant.** `useDeclineCandidateMutation`
   *    advances to the next applicant by replacing the URL, and an email deep
   *    links straight to a candidate. Ignoring it drops the office back on the
   *    list after every decline. A route naming somebody no longer in the list
   *    (declined, withdrawn, or an old email) is ignored: the page would render
   *    the survivor or the list while fetching that person's overtime and
   *    credentials. That is also why nothing is selected until the list loads.
   * 2. **The sole applicant.** They never go through the list, so nothing ever
   *    calls `openCandidate`; and declining down to one survivor would otherwise
   *    leave the declined candidate selected, showing their licences under the
   *    survivor's name.
   * 3. **Nobody.** The page also renders on `/dashboard/job/:jobId`, so a route
   *    that names no candidate is an instruction to show the list — not an
   *    absence of one. Without this the last pick stays on screen while the URL
   *    says otherwise.
   *
   * Applied while rendering, not in an effect, whenever those inputs change: an
   * effect runs after the commit, so a deep link would paint the list for a
   * frame (and query for nobody) before the candidate's page replaced it. Only a
   * change re-applies it, so a row the office opens stays open in between.
   *
   * A change that leaves the route as it was keeps a row the office opened
   * while that candidate is still an applicant: the route candidate withdrawing,
   * or a new applicant arriving, is no reason to close somebody else's page.
   */
  const selectionFromInputs =
    (isRouteCandidateLive ? routeCandidateId : null) ?? soleCandidateId ?? null;
  const selectionInputs = `${isRouteCandidateLive}|${routeCandidateId ?? ''}|${soleCandidateId ?? ''}`;
  const [appliedSelection, setAppliedSelection] = useState<{
    inputs: string;
    routeCandidateId?: string;
  } | null>(null);

  if (appliedSelection?.inputs !== selectionInputs) {
    const isOpenCandidateLive =
      openCandidateId !== null &&
      counters.some((counter) => counter?.candidate?.id === openCandidateId);
    const keepsOfficePick =
      appliedSelection !== null &&
      appliedSelection.routeCandidateId === routeCandidateId &&
      isOpenCandidateLive;

    setAppliedSelection({ inputs: selectionInputs, routeCandidateId });
    setOpenCandidateId(keepsOfficePick ? openCandidateId : selectionFromInputs);
  }

  /**
   * Starts the deep-linked candidate's overtime and credentials while the list
   * is still loading, so a link from an email reads job, then list and
   * candidate together rather than job, then list, then candidate.
   *
   * This only fills the cache: nothing here reads the results, and the
   * selection above still waits for the list. When the candidate is still an
   * applicant the selected reads find the data cached; when the link is stale
   * the page shows nothing fetched for them.
   */
  const isCredentialV2Api = useFeatureFlag(CredentialingV2DNTApi);
  const isCredentialV2 = useFeatureFlag(CredentialingV2);
  const earlyCandidateId = isSuccessFetchJobCandidates ? undefined : routeCandidateId;
  useCandidateOvertime(shiftId, earlyCandidateId, { enabled: job?.id === shiftId });
  useGetAuthorizationCredentials(earlyCandidateId ?? '', isCredentialV2Api || isCredentialV2);

  const activeCounter = isSingleCandidate
    ? counters[0]
    : (counters.find((counter) => counter?.candidate?.id === openCandidateId) ?? null);

  const clearSelection = useCallback(() => setOpenCandidateId(null), []);

  return {
    job: job as Partial<IJob> | undefined,
    authorizations: authorizations as IAuthorization[] | null,
    isLoading: Boolean(loadingCandidates),
    hasLoadedCandidates: Boolean(isSuccessFetchJobCandidates),
    counters,
    isSingleCandidate,
    activeCounter,
    openCandidate: setOpenCandidateId,
    clearSelection,
  };
};
